import React from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '../../../../components';
import { HOME } from '../../../../theme/tokens';
import { Frame, Head, HeadText, Title, Sub, Chev } from './MetricFrame.styles';

export type MetricFrameProps = {
  /** Omitted: no head (e.g. an empty state that carries its own heading, so the focus stays on it). */
  title?: string;
  sub?: string;
  className?: string;
  /** Makes the whole card a button into its full view, with a chevron at the head's right. */
  onOpen?: () => void;
  children: React.ReactNode;
};

/** A Key-Metrics card: the washed zinc surface, a title (+ optional subtitle)
 *  head, then the section body. With `onOpen` the whole card opens its full view. */
export const MetricFrame = ({ title, sub, className, onOpen, children }: MetricFrameProps): React.ReactElement => {
  const { t } = useTranslation();
  const onKey = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (onOpen && event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onOpen();
    }
  };
  return (
    <Frame
      className={className}
      $clickable={!!onOpen}
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      aria-label={onOpen && title ? t('Open {{name}}', { name: title }) : undefined}
      onClick={onOpen}
      onKeyDown={onOpen ? onKey : undefined}
    >
      {title ? (
        <Head>
          <HeadText>
            <Title text={title} tag="h3" />
            {sub ? <Sub text={sub} tag="span" /> : null}
          </HeadText>
          {onOpen ? (
            <Chev aria-hidden="true">
              <Icon name="arrow-right-circle" size={24} color={HOME.t50} />
            </Chev>
          ) : null}
        </Head>
      ) : null}
      {children}
    </Frame>
  );
};

export default MetricFrame;
